import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { themeColors } from 'utils/tailwind/tailwindHelpers';

import {
  useAddInventoryApproval,
  useGetCategoriesByEntity,
  useGetFieldsByCrops,
} from '@/modules/settings/approvals/apiHooks';
import { useInventoryApprovals } from '@/modules/settings/approvals/components/Inventory/logic';
import EditableApproverRows from '@/modules/settings/approvals/components/shared/ApprovalDetails/ApproverUsersList/EditableApproverRows';
import ProgressBar from '@/modules/settings/approvals/components/shared/ProgressBar';
import TableHeader from '@/modules/settings/approvals/components/shared/TableHeader';
import { IApprovals } from '@/modules/settings/approvals/types';
import {
  Button,
  CircularBgImage,
  InputField,
  SelectField,
} from '@/shared/components/atoms';
import InfiniteSelectField from '@/shared/components/atoms/SelectField/InfiniteSelectField';
import { AssetItemProps } from '@/shared/components/molecules/ChooseAsset/ChooseAsset';
import { useStore } from '@/shared/hooks/context/createGenericContext';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';
import { IGeneral } from '@/shared/types';

const CreateEditApproval = () => {
  /******************** Hooks ********************/
  const { t } = useTranslation();
  const { closeModal } = useModal();
  const {
    getFieldsOptions,
    addInventoryApprovalPathSchema,
    getAddApprovalMutation,
  } = useInventoryApprovals();

  /******************** Form ********************/
  const formMethods = useForm<IApprovals.ICreateInventoryApprovalForm>({
    mode: 'all',
    defaultValues: {
      approvers: [
        {
          canEdit: 1,
        },
      ],
    },
    resolver: yupResolver(addInventoryApprovalPathSchema),
  });
  const {
    formState: { errors },
    register,
    watch,
    handleSubmit,
    trigger,
  } = formMethods;

  /******************** Data fetching ********************/
  const entityId = watch('entityId');
  const [assetsList] = useStore((store) => store?.assetsList);
  const { data: fields } = useGetFieldsByCrops(entityId);
  const { data: categories, fetchNextPage } = useGetCategoriesByEntity({
    entityId,
    page: 1,
    limit: 10,
  });
  const categoriesList = categories?.pages.flatMap((page) => page.items);

  /******************** State management ********************/
  const [step, setStep] = useState(1);
  const handleStepChange = async () => {
    const isFirstStepValid = await trigger([
      'approvalCycleCategories',
      'approvalCyclePlots',
      'entityId',
      'name',
      'notes',
    ]);
    if (isFirstStepValid) setStep(2);
  };

  /******************** Mutation ********************/
  const { mutate } = useAddInventoryApproval();
  const onSubmit: SubmitHandler<IApprovals.ICreateInventoryApprovalForm> = (
    values
  ) => {
    mutate(getAddApprovalMutation(values), {
      onSuccess: () => {
        toast.success(t('approval-path-added-successfully'));
        closeModal();
      },
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className='max-h-640 w-[800px] scrollbar'
      >
        {/******************** Progress bars ********************/}
        <ProgressBar className='mb-32' step={step} />
        {/******************** step 1 ********************/}
        {step === 1 && (
          <>
            <div className='mb-24 grid grid-cols-2 gap-24'>
              <SelectField
                isSearchable
                width='w-full'
                borderColor={themeColors().grey[200]}
                borderRadius='1rem'
                id='entityId'
                py='9px'
                label={t('Job Location')}
                placeholder={t('Job Location')}
                list={[
                  ...(assetsList?.farms.filter(
                    (farm) => farm.inventoryApproval
                  ) || []),
                ]}
                getLabel={(option: AssetItemProps) => option.name}
                getValue={(option: AssetItemProps) => option.id}
              />
              <InputField
                borderColor='border-outline-variant'
                width='w-full'
                py='!py-16'
                label={t('approval-path-name')}
                placeholder={t('approval-path-name')}
                error={!!errors.name}
                errorMessage={errors.name?.message}
                className='!rounded-xl bg-background-transparent'
                // disabled={isLoading}
                {...register('name')}
              />
              <SelectField<
                IGeneral.ICropSchema,
                true,
                IApprovals.IGroupBaseWithImage
              >
                isSearchable
                isGroup
                isMulti
                isDisabled={!entityId}
                width='w-full'
                borderColor={themeColors().grey[200]}
                borderRadius='1rem'
                id='approvalCyclePlots'
                py='9px'
                label={t('work-locations')}
                placeholder={t('work-locations')}
                list={fields}
                options={getFieldsOptions(fields)}
                noOptionsMessage={() => t('no-results-message')}
                getLabel={(option: IGeneral.ICropSchema) => option.name}
                getValue={(option: IGeneral.ICropSchema) => option.id}
                formatGroupLabel={(group: IApprovals.IGroupBaseWithImage) => (
                  <div className='flex items-center gap-1'>
                    {group.image && <CircularBgImage imageSrc={group.image} />}
                    {group.label}
                  </div>
                )}
                formatOptionLabel={(
                  option: IGeneral.ICropSchema,
                  { context }
                ) =>
                  context === 'value' ? (
                    <div className='flex items-center gap-1'>
                      {option.image && (
                        <CircularBgImage imageSrc={option.image} />
                      )}
                      {option.name}
                    </div>
                  ) : (
                    option.name
                  )
                }
              />
              <InfiniteSelectField
                isMulti
                isSearchable
                isDisabled={!entityId}
                width='w-full'
                borderColor={themeColors().grey[200]}
                borderRadius='1rem'
                id='approvalCycleCategories'
                py='9px'
                label={t('product-type')}
                placeholder={t('product-type')}
                fetchNextPage={fetchNextPage}
                list={categoriesList}
                getLabel={(option: IGeneral.NameIdPair) => option.name}
                getValue={(option: IGeneral.NameIdPair) => option.id}
              />
            </div>
            <InputField
              borderColor='border-outline-variant'
              width='w-full'
              multiline
              rows={4}
              placeholder={t('notes')}
              error={!!errors.notes}
              errorMessage={errors.notes?.message}
              className='!rounded-xl bg-background-transparent'
              // disabled={isLoading}
              {...register('notes', {
                setValueAs: (value) => (value?.length ? value : undefined),
              })}
            />
          </>
        )}

        {/******************** step 2 ********************/}
        {step === 2 && (
          <>
            <TableHeader />
            <EditableApproverRows approvalType='inventory' />
          </>
        )}

        {/******************** actions ********************/}
        <div className='mt-48 flex justify-end'>
          {step === 1 ? (
            <Button className='h-56 !rounded-xl' onClick={handleStepChange}>
              {t('next')}
            </Button>
          ) : (
            <div className='flex gap-2'>
              <Button
                variant='text'
                type='button'
                size='md'
                className='h-56 w-full !rounded-xl border-xs border-grey-200'
                onClick={() => setStep(1)}
              >
                {t('back')}
              </Button>
              <Button className='h-56 !rounded-xl' type='submit'>
                {t('save')}
              </Button>
            </div>
          )}
        </div>
      </form>
    </FormProvider>
  );
};

export default CreateEditApproval;
